import React, { useCallback, useMemo, useState } from 'react';

import { CustomOperatorValue } from '@aline/common';
import { Menu, MenuItem, Skeleton, Typography } from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import type { GridFilterModel } from '@mui/x-data-grid-premium';
import { AlineCheckbox, DropdownSelectButton, Spacer } from 'src/components';
import type { RowStatusFilterItem } from 'src/components/generic/data-grid/custom-operators';
import { getIsRowStatusFilterItem } from 'src/components/generic/data-grid/custom-operators';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { StatusDot } from './StatusDot';

const ALL_STATUS = 'All' as const;
type StatusType = Apollo.RowStatus | typeof ALL_STATUS;

interface AiStatusFilterProps {
  reportId: string;
}

const formatStatusName = (status: Apollo.RowStatus): string => {
  switch (status) {
    case Apollo.RowStatus.AiNotRun:
      return 'AI Not Run';
    case Apollo.RowStatus.AiError:
      return 'AI Error';
    case Apollo.RowStatus.OcrError:
      return 'OCR Error';
    case Apollo.RowStatus.AiRunning:
      return 'AI Thinking';
    case Apollo.RowStatus.AnswerStale:
      return 'Answer Stale';
    case Apollo.RowStatus.OcrRunning:
      return 'OCR Running';
  }
};

const useStyles = makeStyles()(theme => ({
  checkbox: {
    padding: 0,
  },
  menuItem: {
    gap: theme.spacing(1),
  },
  menuPaper: {
    minWidth: 240,
  },
  statusCount: {
    backgroundColor: '#ed6c02',
    borderRadius: 4,
    padding: theme.spacing(0.25, 0.5),
  },
  statusCountText: {
    color: theme.palette.common.white,
    fontSize: 10,
  },
  statusLabel: {
    fontWeight: 600,
  },
  statusLabelContainer: {
    alignItems: 'baseline',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
}));

function useGetReportMeta(
  reportId: string,
  variables: Omit<Apollo.GetRowsMetaQueryVariables, 'reportId'>
) {
  const [getReportMetaQuery, { data, loading, refetch }] = Apollo.useGetRowsMetaLazyQuery({
    fetchPolicy: 'network-only',
    // Necessary to make loading: true when refetching...
    notifyOnNetworkStatusChange: true,
  });
  const getReportMeta = useCallback(
    () => getReportMetaQuery({ variables: { reportId, ...variables } }),
    [getReportMetaQuery, reportId, variables]
  );
  const defaultMeta: Apollo.RowsMeta = {
    __typename: 'RowsMeta',
    aiErrorCount: 0,
    aiNotRunCount: 0,
    aiRunningCount: 0,
    answerStaleCount: 0,
    ocrErrorCount: 0,
    ocrRunningCount: 0,
  };

  return {
    getReportMeta,
    loading,
    meta: data?.report?.rowsMeta ?? defaultMeta,
    refetch,
  };
}

/**
 * Custom hook to extract RowStatus filter values from the filterModel using useGridSelector.
 */
function useRowStatusFromFilterModel(): {
  filterModel: GridFilterModel;
  rowStatusFilterItem: RowStatusFilterItem | null;
} {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const rowStatusFilterItem = useMemo(() => {
    return filterModel?.items?.find(getIsRowStatusFilterItem) ?? null;
  }, [filterModel]);

  return {
    filterModel,
    rowStatusFilterItem,
  };
}

export const AiStatusFilter: React.FC<AiStatusFilterProps> = ({ reportId }) => {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { filterModel, rowStatusFilterItem } = useRowStatusFromFilterModel();
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const selectedStatuses = rowStatusFilterItem?.value ?? [];

  /**
   * Scope-aware breakdown: pass the grid's active filterModel to GetRowsMeta
   * so the dropdown counts reflect the rows currently visible in the grid
   * (e.g., when the user has applied a search or folder filter). Excludes
   * the rowStatus filter item itself so selecting "AI Error" doesn't zero
   * out the other status counts in the dropdown — that would defeat the
   * purpose of the picker.
   */
  const scopedFilterModel = useMemo(
    () => ({
      ...filterModel,
      items: filterModel.items.filter(item => !getIsRowStatusFilterItem(item)),
    }),
    [filterModel]
  );
  const metaVariables = useMemo(
    () => ({ filterModel: scopedFilterModel, filterTimeZone }),
    [filterTimeZone, scopedFilterModel]
  );
  const { getReportMeta, loading, meta } = useGetReportMeta(reportId, metaVariables);

  const getStatusCount = (status: Apollo.RowStatus): number => {
    const countMap: Record<Apollo.RowStatus, number> = {
      [Apollo.RowStatus.AiError]: meta.aiErrorCount,
      [Apollo.RowStatus.AiNotRun]: meta.aiNotRunCount,
      [Apollo.RowStatus.AiRunning]: meta.aiRunningCount,
      [Apollo.RowStatus.AnswerStale]: meta.answerStaleCount,
      [Apollo.RowStatus.OcrError]: meta.ocrErrorCount,
      [Apollo.RowStatus.OcrRunning]: meta.ocrRunningCount,
    };
    return countMap[status];
  };

  const handleStatusToggle = (status: StatusType) => {
    const newSelectedStatuses =
      status === ALL_STATUS
        ? []
        : selectedStatuses.includes(status)
          ? selectedStatuses.filter(s => s !== status)
          : [...selectedStatuses, status];
    const otherFilters = filterModel.items.filter(item => !getIsRowStatusFilterItem(item));

    apiRef.current.setFilterModel({
      ...filterModel,
      items:
        newSelectedStatuses.length > 0
          ? [
              ...otherFilters,
              {
                field: 'rowStatus',
                operator: CustomOperatorValue.RowStatusAnyOf,
                value: newSelectedStatuses,
              },
            ]
          : otherFilters,
    });
  };

  const getStatusListString = (): string => {
    if (!selectedStatuses.length) return 'All';
    if (selectedStatuses.length <= 2) return selectedStatuses.map(formatStatusName).join(', ');

    return `${selectedStatuses.slice(0, 2).map(formatStatusName).join(', ')}, +${
      selectedStatuses.length - 2
    }`;
  };

  return (
    <div>
      <DropdownSelectButton
        ref={setAnchorEl}
        label={
          <div className={classes.statusLabelContainer}>
            <Typography
              noWrap
              sx={{
                display: 'inline',
              }}
            >
              {rowStatusFilterItem?.operator === CustomOperatorValue.RowStatusNotAnyOf
                ? 'Status Not: '
                : 'Status: '}
            </Typography>
            <Typography
              noWrap
              className={classes.statusLabel}
              sx={{
                display: 'inline',
              }}
            >
              {getStatusListString()}
            </Typography>
          </div>
        }
        selected={open}
        variant="outlined"
        onClick={() => setOpen(!open)}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        classes={{ paper: classes.menuPaper }}
        open={open}
        slotProps={{
          transition: { onEnter: () => getReportMeta() },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        variant="menu"
        onClose={() => setOpen(false)}
      >
        {Object.values(Apollo.RowStatus)
          .sort((a, b) => a.localeCompare(b))
          .map(status => {
            const statusCount = getStatusCount(status);

            return (
              <MenuItem
                key={status}
                dense
                className={classes.menuItem}
                onClick={() => handleStatusToggle(status)}
              >
                <AlineCheckbox
                  checked={selectedStatuses.includes(status)}
                  className={classes.checkbox}
                  size="small"
                />
                <StatusDot status={status} />
                <Typography noWrap className={classes.statusLabel}>
                  {formatStatusName(status)}
                </Typography>
                <Spacer />
                <StatusCount loading={loading} statusCount={statusCount} />
              </MenuItem>
            );
          })}
      </Menu>
    </div>
  );
};

function StatusCount({ loading, statusCount }: { loading: boolean; statusCount: number }) {
  const { classes } = useStyles();

  if (loading) return <Skeleton height={20} variant="rectangular" width={20} />;

  return (
    <div
      className={classes.statusCount}
      style={{ visibility: statusCount > 0 ? 'visible' : 'hidden' }}
    >
      <Typography className={classes.statusCountText}>
        {new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 2,
          notation: 'compact',
        }).format(statusCount)}
      </Typography>
    </div>
  );
}
